import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { UploadDialog } from "@/components/UploadDialog";
import { MediaLightbox } from "@/components/MediaLightbox";
import { WorkQueue } from "@/components/WorkQueue";
import { CollectionsRow } from "@/components/CollectionsRow";
import { SmartCover } from "@/components/SmartCover";
import { api, resolveAvatar, fileUrl, thumbUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Star, ImageIcon, Award, Eye, Camera, Pencil, Trash2, Link as LinkIcon, MessageSquare, Save, X as XIcon, Shield, Layers, Sparkles, MapPin, Upload, Inbox } from "lucide-react";
import { Badges } from "@/components/Badges";
import { cn } from "@/lib/utils";

const Profile = () => {
  const { user, loading: authLoading, openPersona, requireAction, requireUpload, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [myMedia, setMyMedia] = useState([]);
  const [selected, setSelected] = useState(null);
  const [queueKey, setQueueKey] = useState(0);  // bump after any edit so queue counts refresh from the server
  const [links, setLinks] = useState([]);
  const [dmsEnabled, setDmsEnabled] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [collections, setCollections] = useState([]);
  const [smartGals, setSmartGals] = useState([]);
  const avatarRef = useRef(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  const changeAvatar = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image")) { toast.error("Pick an image"); return; }
    setAvatarUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post("/profile/avatar/set", form, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((p) => ({ ...p, avatar_url: r.data.avatar_url, avatar_type: "custom" }));
      checkAuth?.();
      toast.success("Profile picture updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setAvatarUploading(false); }
  };

  const loadCollections = useCallback(async () => {
    try { const r = await api.get("/collections/mine"); setCollections(r.data); } catch {}
    if (user?.is_admin) { try { const s = await api.get("/collections/system"); setSmartGals(s.data || []); } catch {} }
  }, [user]);

  const load = useCallback(async () => {
    // no setLoading(true) here: `loading` starts true and only covers the FIRST load. A refresh (e.g. after an upload) updates in
    // place — the full-page spinner would unmount the open UploadDialog and wipe its result cards.
    try {
      const [p, m] = await Promise.all([api.get("/profile/me"), api.get("/my/media")]);
      setProfile(p.data);
      setMyMedia(m.data);
      setLinks(p.data.links?.length ? p.data.links : [""]);
      setDmsEnabled(p.data.dms_enabled !== false);
      loadCollections();
    } finally {
      setLoading(false);
    }
  }, [loadCollections]);

  const savePrefs = async (nextLinks, nextDms) => {
    setSavingPrefs(true);
    try {
      const r = await api.put("/profile/prefs", {
        links: (nextLinks ?? links).filter((l) => l.trim()),
        dms_enabled: nextDms ?? dmsEnabled,
      });
      setLinks(r.data.links.length ? r.data.links : [""]);
      toast.success("Settings saved");
    } catch { toast.error("Could not save"); } finally { setSavingPrefs(false); }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    load();
  }, [user, authLoading, load, navigate]);

  const deleteAccount = async () => {
    if (!window.confirm("Delete your account and all your content permanently? This cannot be undone.")) return;
    try {
      await api.delete("/account");
      toast.success("Your account has been deleted");
      window.location.href = "/";
    } catch (e) {
      toast.error("Could not delete account");
    }
  };

  // refresh when persona dialog closes
  useEffect(() => {
    if (user?.profile_complete) load();
  }, [user, load]);

  if (authLoading || loading || !profile) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const stats = [
    { label: "Rank", value: profile.rank, icon: Award, big: true },
    { label: "Rated", value: profile.ratings_count, icon: Star },
    { label: "Uploads", value: profile.uploads_count, icon: ImageIcon },
  ];

  return (
    <div className="min-h-screen relative z-10">
      <Navbar onUpload={() => requireUpload() && setUploadOpen(true)} />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        {/* WORK AREA FIRST: what needs attention → upload/manage → recent → then profile/stats */}
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-6">
          <h1 data-testid="dashboard-title" className="font-heading text-4xl sm:text-5xl font-light tracking-tighter">{user?.is_admin ? "Dashboard" : "My Page"}</h1>
          <span className="text-xs text-muted-foreground">{user?.is_admin ? "Owner work center — queues, uploads, content" : "Your uploads, requests and profile"}</span>
        </div>
        {user?.is_admin ? (
          <WorkQueue onUpload={() => requireUpload() && setUploadOpen(true)} onOpenMedia={(id) => setSelected(id)} refreshKey={queueKey} />
        ) : (
        <div data-testid="profile-actions" className="flex flex-wrap gap-3">
          <Button data-testid="profile-upload" size="lg" onClick={() => requireUpload() && setUploadOpen(true)} className="rounded-full font-semibold shadow-lg shadow-primary/20 hover:-translate-y-0.5 transition-transform">
            <Upload className="w-4 h-4 mr-2" /> Submit / Upload
          </Button>
          <Button data-testid="profile-my-uploads" size="lg" onClick={() => navigate("/my-uploads")} variant="outline" className="rounded-full border-border">
            <ImageIcon className="w-4 h-4 mr-2" /> My Uploads
          </Button>
          <Button data-testid="profile-requests" size="lg" onClick={() => navigate("/requests")} variant="outline" className="rounded-full border-border">
            <Inbox className="w-4 h-4 mr-2" /> Requests
          </Button>
          <Button data-testid="profile-browse" size="lg" onClick={() => navigate("/browse")} variant="outline" className="rounded-full border-border">
            Browse the gallery
          </Button>
        </div>
        )}

        <h2 className="font-heading text-2xl mt-12 mb-4 text-muted-foreground">My Content &amp; Profile</h2>
        {user?.is_admin &&         <div data-testid="profile-actions-secondary" className="flex flex-wrap gap-3 mb-8">
          <Button data-testid="profile-upload" size="lg" onClick={() => requireUpload() && setUploadOpen(true)} className="rounded-full font-semibold shadow-lg shadow-primary/20 hover:-translate-y-0.5 transition-transform">
            <Upload className="w-4 h-4 mr-2" /> Submit / Upload
          </Button>
          <Button data-testid="profile-my-uploads" size="lg" onClick={() => navigate("/my-uploads")} variant="outline" className="rounded-full border-border">
            <ImageIcon className="w-4 h-4 mr-2" /> My Uploads
          </Button>
          <Button data-testid="profile-requests" size="lg" onClick={() => navigate("/requests")} variant="outline" className="rounded-full border-border">
            <Inbox className="w-4 h-4 mr-2" /> Requests
          </Button>
          <Button data-testid="profile-browse" size="lg" onClick={() => navigate("/browse")} variant="outline" className="rounded-full border-border">
            Browse the gallery
          </Button>
        </div>}
        <div className="flex flex-col sm:flex-row gap-8 items-start">
          <div className="relative shrink-0">
            <button type="button" data-testid="profile-avatar-change" onClick={() => avatarRef.current?.click()}
              className="group relative block w-40 h-40 rounded-2xl overflow-hidden border border-border bg-secondary">
              {resolveAvatar(profile.avatar_url) ? (
                <img data-testid="profile-avatar" src={resolveAvatar(profile.avatar_url)} alt={profile.display_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl font-heading">{(profile.display_name || profile.name)?.[0]}</div>
              )}
              <span className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 text-white">
                {avatarUploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Camera className="w-6 h-6" /><span className="text-xs font-medium">Change photo</span></>}
              </span>
            </button>
            <input ref={avatarRef} type="file" accept="image/*" className="hidden" data-testid="profile-avatar-input"
              onChange={(e) => { changeAvatar(e.target.files?.[0]); e.target.value = ""; }} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 data-testid="profile-name" className="font-heading text-4xl font-light tracking-tighter">{profile.display_name || profile.name}</h1>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {profile.is_admin ? (
                    <span data-testid="profile-owner-badge" className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border border-primary/50 text-primary bg-primary/10">
                      <Shield className="w-3 h-3" /> Owner
                    </span>
                  ) : profile.role && (
                    <span className={cn("flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border",
                      profile.role === "contributor" ? "border-primary/40 text-primary bg-primary/10" : "border-border text-muted-foreground")}>
                      {profile.role === "contributor" ? <Camera className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      {profile.role === "contributor" ? "Contributor" : "Viewer"}
                    </span>
                  )}
                  {profile.gender && <span className="text-xs uppercase tracking-widest text-muted-foreground capitalize">{profile.gender}</span>}
                  {profile.age && !profile.is_admin && <span className="text-xs uppercase tracking-widest text-muted-foreground">· {profile.age}</span>}
                </div>
                <div data-testid="profile-account-debug" className="mt-2 text-[11px] font-mono text-muted-foreground">
                  Signed in as {profile.email} · owner/admin: <span className={profile.is_admin ? "text-emerald-400" : "text-red-400"}>{profile.is_admin ? "YES" : "NO"}</span>
                </div>
              </div>
              <Button data-testid="profile-edit-btn" variant="outline" onClick={openPersona} className="rounded-full border-border shrink-0">
                <Pencil className="w-4 h-4 mr-1.5" /> Edit
              </Button>
            </div>
            {profile.bio && <p className="text-muted-foreground mt-4 leading-relaxed">{profile.bio}</p>}
            <Badges badges={profile.badges} editable onToggle={async (key) => {
              const updated = profile.badges.map((b) => b.key === key ? { ...b, hidden: !b.hidden } : b);
              setProfile((p) => ({ ...p, badges: updated }));
              try { await api.post("/me/badges", { hidden: updated.filter((b) => b.hidden).map((b) => b.key) }); } catch {}
            }} />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-10">
          {stats.map((s) => (
            <div key={s.label} data-testid={`profile-stat-${s.label.toLowerCase()}`} className="rounded-lg border border-border bg-card p-5">
              <s.icon className="w-5 h-5 text-primary mb-3" />
              <div className={cn("font-semibold", s.big ? "font-heading text-xl" : "font-mono text-3xl")}>{s.value}</div>
              <div className="text-xs tracking-widest uppercase text-muted-foreground mt-1">{s.label}</div>
            </div>
          ))}
        </div>


        {profile.featured && (
          <div className="mt-12">
            <h2 className="font-heading text-2xl mb-4 flex items-center gap-2"><Star className="w-5 h-5 text-primary fill-primary" /> Featured Gallery</h2>
            <div className="max-w-xs"><CollectionsRow collections={[profile.featured]} editable={false} /></div>
          </div>
        )}

        <div className="mt-12">
          <h2 className="font-heading text-2xl mb-4">My Galleries <span className="text-muted-foreground text-base">({collections.length})</span></h2>
          <p className="text-sm text-muted-foreground mb-4 -mt-2">Curate your favourite photos & videos. Public by default — flip any gallery to private. Open a gallery to pin it here. Your own galleries come first, then your smart galleries.</p>
          <CollectionsRow collections={collections} editable onCreated={(c) => setCollections((p) => [c, ...p])} />
        </div>

        {user?.is_admin && smartGals.length > 0 && (
          <div className="mt-12" data-testid="profile-smart-galleries">
            <h2 className="font-heading text-2xl mb-1 flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Auto Smart Galleries</h2>
            <p className="text-sm text-muted-foreground mb-4">Auto-updating collections built from your tags. The Inbox holds anything still untagged.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {smartGals.filter((g) => g.system_key === "inbox").map((inbox) => (
                <button key={inbox.id} data-testid="profile-smart-inbox" onClick={() => navigate(`/collection/${inbox.id}`)}
                  className="flex items-center gap-4 rounded-xl border border-primary/40 bg-primary/10 hover:bg-primary/20 transition-colors p-4 text-left">
                  <div className="w-16 h-16 rounded-lg bg-primary/20 flex items-center justify-center shrink-0"><Layers className="w-8 h-8 text-primary" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xl font-heading">Inbox — needs review</div>
                    <div className="text-sm text-muted-foreground">{inbox.count} photo{inbox.count === 1 ? "" : "s"} still needing a title, description, owner tags, or location — open, review, Save to clear.</div>
                  </div>
                  <span className="text-2xl font-bold text-primary">{inbox.count}</span>
                </button>
              ))}
              {smartGals.filter((g) => g.system_key === "no-location").map((noloc) => (
                <button key={noloc.id} data-testid="profile-smart-no-location" onClick={() => navigate(`/collection/${noloc.id}`)}
                  className="flex items-center gap-4 rounded-xl border border-amber-400/40 bg-amber-400/10 hover:bg-amber-400/20 transition-colors p-4 text-left">
                  <div className="w-16 h-16 rounded-lg bg-amber-400/20 flex items-center justify-center shrink-0"><MapPin className="w-8 h-8 text-amber-400" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xl font-heading">No Location</div>
                    <div className="text-sm text-muted-foreground">{noloc.count} item{noloc.count === 1 ? "" : "s"} with no place — add a city to put it on the map.</div>
                  </div>
                  <span className="text-2xl font-bold text-amber-400">{noloc.count}</span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
              {smartGals.filter((g) => g.system_key !== "inbox" && g.system_key !== "no-location").map((g) => { const cover = (g.thumbs || [])[0]; return (
                <button key={g.id} data-testid={`profile-smart-${g.system_key || g.id}`} onClick={() => navigate(`/collection/${g.id}`)}
                  className="text-left rounded-lg border border-border bg-secondary/40 overflow-hidden hover:border-primary/50 transition-colors">
                  <div className="aspect-square bg-black relative">
                    {cover && cover.media_type === "image" ? <SmartCover src={thumbUrl(cover.file_ref, 400)} frame={cover.frame} aspect={1} /> : cover && cover.media_type === "video" && cover.poster ? <SmartCover src={cover.poster} frame={cover.frame} aspect={1} /> : <div className="w-full h-full flex items-center justify-center"><Layers className="w-5 h-5 text-muted-foreground" /></div>}
                  </div>
                  <div className="p-1.5">
                    <div className="text-xs font-medium truncate">{g.title || g.name}</div>
                    <div className="text-[10px] text-muted-foreground">{g.count} items</div>
                  </div>
                </button>
              ); })}
            </div>
          </div>
        )}

        {/* Settings + Danger Zone — hidden for the owner/admin */}
        {!profile.is_admin && (<>
        <div className="mt-12 rounded-xl border border-border bg-card p-5" data-testid="profile-settings">
          <h3 className="font-heading text-lg">Promote your sites &amp; socials</h3>
          <p className="text-sm text-muted-foreground mt-1">Add up to 6 links (your site, Instagram, X, OnlyFans, Linktree…). Shown to signed-in members on your page so fans can find you everywhere.</p>
          <div className="space-y-2 mt-4">
            {links.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                <input data-testid={`profile-link-${i}`} value={l} onChange={(e) => setLinks(links.map((x, idx) => idx === i ? e.target.value : x))}
                  placeholder="instagram.com/yourhandle" className="flex-1 bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-primary" />
                {links.length > 1 && <button data-testid={`profile-link-remove-${i}`} onClick={() => setLinks(links.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-destructive"><XIcon className="w-4 h-4" /></button>}
              </div>
            ))}
            {links.length < 6 && <button data-testid="profile-link-add" onClick={() => setLinks([...links, ""])} className="text-sm text-primary hover:underline">+ Add another link</button>}
          </div>
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
            <div className="flex items-center gap-2 text-sm"><MessageSquare className="w-4 h-4 text-muted-foreground" /> Allow members to DM me</div>
            <button data-testid="profile-dms-toggle" onClick={() => { const n = !dmsEnabled; setDmsEnabled(n); savePrefs(undefined, n); }}
              className={cn("w-11 h-6 rounded-full transition-colors relative", dmsEnabled ? "bg-primary" : "bg-secondary")}>
              <span className={cn("absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all", dmsEnabled ? "left-5" : "left-0.5")} />
            </button>
          </div>
          <Button data-testid="profile-prefs-save" onClick={() => savePrefs()} disabled={savingPrefs} className="mt-4 rounded-full">
            {savingPrefs ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Save links</>}
          </Button>
        </div>

        <div className="mt-8 rounded-xl border border-destructive/30 bg-destructive/5 p-5">
          <h3 className="font-heading text-lg text-red-400">Danger Zone</h3>
          <p className="text-sm text-muted-foreground mt-1">Deleting your account removes your profile, all your posts, comments and votes. This cannot be undone.</p>
          <Button data-testid="delete-account-btn" onClick={deleteAccount} variant="outline"
            className="mt-3 rounded-full border-destructive/50 text-red-400 hover:bg-destructive/10 hover:text-red-300">
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete my account
          </Button>
        </div>
        </>)}
      </div>

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={load} />
      <MediaLightbox mediaId={selected} open={!!selected} onClose={() => { setSelected(null); setQueueKey((k) => k + 1); }} onChanged={() => { load(); setQueueKey((k) => k + 1); }} onNavigate={setSelected} playlist={myMedia} />
    </div>
  );
};

export default Profile;
